import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Divider,
} from "@mui/material";
import { useEffect, useState, type ChangeEvent } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CancelIcon from "@mui/icons-material/Cancel";

import Backdrop from "@mui/material/Backdrop";
import imageCompression from "browser-image-compression";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { v4 as uuidv4 } from "uuid";
import { autoRedirectToInventory } from "../../redux/action/inventory";

interface PayloadObject {
  title: string;
  images: File[]; // Specify the type as File[]
}

type AccessInfoField =
  | "originStairs"
  | "originElevator"
  | "originLongCarry"
  | "destinationStairs"
  | "destinationElevator"
  | "destinationLongCarry";

const AddSupermoveInventory = () => {
  const { phoneNumber } = useParams<{ phoneNumber: string }>();

  const { identifier } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [imagePreviews, setImagePreviews] = useState<string[][]>([]);
  const [payloadObjects, setPayloadObjects] = useState<PayloadObject[]>([
    { title: "", images: [] },
  ]);
  const [formError, setFormError] = useState("");
  const [sessionId, setSessionId] = useState(uuidv4());
  const [accessInfo, setAccessInfo] = useState<Record<AccessInfoField, string>>(
    {
      originStairs: "",
      originElevator: "",
      originLongCarry: "",
      destinationStairs: "",
      destinationElevator: "",
      destinationLongCarry: "",
    }
  );

  useEffect(() => {
    setIsLoading(false);
    const body = {
      phone: phoneNumber,
    };
    const { phone } = body;

    if (phoneNumber === phone) {
      dispatch<any>(autoRedirectToInventory(body))
        .unwrap()
        .then(() => {})
        .catch((err: any) => {
          console.log(err);
        });
    }
  }, []);

  const handleUploadImages = async () => {
    if (isLoading) return;
    setFormError("");
    setIsLoading(true);
    setLoadingMessage("Submitting...");
    const supermoveActions = require("../../redux/action/supermove") as typeof import("../../redux/action/supermove");
    const {
      fetchSupermoveInventoryFromImage,
      sendInventoryToSuperMove,
      updateMoveDetails,
    } = supermoveActions;
    try {
      if (identifier && phoneNumber) {
        await dispatch<any>(
          updateMoveDetails({
            identifier,
            phoneNumber,
            body: buildMoveDetailsBody(),
          })
        ).unwrap();
      } else {
        console.error("Missing identifier or phone number for move details.");
        setFormError("Missing identifier or phone number.");
        setIsLoading(false);
        setLoadingMessage("");
        return;
      }
    } catch (error) {
      console.error("Error saving move details:", error);
      setIsLoading(false);
      setLoadingMessage("");
      setFormError("Failed to save move details. Please try again.");
      return;
    }

    if (
      payloadObjects.every(
        (payload) =>
          payload.title &&
          payload.images.length &&
          payload.images.every((img) => img.type)
      )
    ) {
      setLoadingMessage("Uploading image(s). Please wait...");

      let totalImages = payloadObjects.reduce(
        (acc, payload) => acc + payload.images.length,
        0
      );
      let currentImageCount = 0;
      let inventoryData = [];
      let totalVolumeFromAllImages = 0;
      let inventoryIdFromResponse: number | null = null;
      let allInventoryData: any[] = [];

      for (let i = 0; i < payloadObjects.length; i++) {
        for (let j = 0; j < payloadObjects[i].images.length; j++) {
          const formData = new FormData();
          formData.append("phoneNumber", phoneNumber || "");
          formData.append("identifier", identifier || "");
          const roomsArray = [{ roomName: payloadObjects[i].title }];
          formData.append("rooms", JSON.stringify(roomsArray));
          formData.append("file", payloadObjects[i].images[j]);

          formData.append("currentCount", String(currentImageCount + 1));
          formData.append("totalCount", String(totalImages));
          formData.append("sessionId", sessionId);
          try {
            const result = await dispatch<any>(
              fetchSupermoveInventoryFromImage(formData)
            ).unwrap();
            totalVolumeFromAllImages += result.totalVolume;
            inventoryIdFromResponse = result.inventoryId;
            if (result.inventoryData) {
              allInventoryData.push(...result.inventoryData);
            }
            currentImageCount += 1;
            inventoryData.push({
              roomName: payloadObjects[i].title,
              image: payloadObjects[i].images[j],
              additionalInventoryData: result,
            });
          } catch (error) {
            console.error("Error uploading image:", error);
            setLoadingMessage("Error processing some images.");
          }
        }
      }
      setLoadingMessage("All images uploaded successfully!");
      const body = {
        identifier: identifier,
        phoneNumber: phoneNumber,
        sessionId: sessionId,
      };
      console.log("sending to supermove");
      setLoadingMessage("Finalizing. Please wait...");
      try {
        await dispatch<any>(sendInventoryToSuperMove(body)).unwrap();
        navigate("/supermove-images/success-message");
      } catch (err) {
        console.error(err);
        setIsLoading(false);
        setLoadingMessage("");
        setFormError("Failed to finalize inventory. Please try again.");
      }
    } else {
      setFormError(
        "Please ensure each room has a name, at least one valid image, and select a stuff factor"
      );
      setIsLoading(false);
      setLoadingMessage("");
    }
  };
  // Function to handle title change
  const handleTitleChange1 = (index: any, value: any) => {
    setFormError("");
    const updatedPayloadObjects = [...payloadObjects];
    updatedPayloadObjects[index].title = value;
    setPayloadObjects(updatedPayloadObjects);
  };
  const addPayloadObject = () => {
    setPayloadObjects([...payloadObjects, { title: "", images: [] }]);
  };
  // Function to handle adding an image to a payload object
  const addImageToPayload = async (index: any, image: any, imageIndex: any) => {
    setFormError("");
    setIsLoading(true);
    setLoadingMessage("Compressing Image...");
    const updatedPayloadObjects: PayloadObject[] = [...payloadObjects]; // Specify the type of updatedPayloadObjects
    const file: File = image.target.files[0]; // Specify the type of file

    const previewUrl = URL.createObjectURL(file);

    // Options for image compression
    const options = {
      maxSizeMB: 3,
      maxWidthOrHeight: 2000,
      useWebWorker: true,
    };

    // Compress the image file
    const compressedFile = await imageCompression(file, options);
    if (compressedFile) {
      // Update the payload with the compressed file
      updatedPayloadObjects[index].images[imageIndex] = compressedFile;
      setPayloadObjects(updatedPayloadObjects);
    } else {
      updatedPayloadObjects[index].images[imageIndex] = file;
      setPayloadObjects(updatedPayloadObjects);
    }

    const updatedPreviews = [...imagePreviews];
    if (!updatedPreviews[index]) updatedPreviews[index] = [];
    updatedPreviews[index][imageIndex] = previewUrl;
    setImagePreviews(updatedPreviews);

    setIsLoading(false);
    setLoadingMessage("");
  };
  const addImageInput = (index: number) => {
    const updatedPayloadObjects = [...payloadObjects];
    if (
      updatedPayloadObjects[index].images.length === 0 ||
      updatedPayloadObjects[index].images.slice(-1)[0].type !== undefined
    ) {
      updatedPayloadObjects[index].images.push(new File([], ""));
      setPayloadObjects(updatedPayloadObjects);
    }
  };
  const discardRoom = (index: number) => {
    const updatedPayloadObjects = [...payloadObjects];
    updatedPayloadObjects.splice(index, 1);
    setPayloadObjects(updatedPayloadObjects);
  };

  const discardImage = (roomIndex: number, imageIndex: number) => {
    const updatedPayloadObjects = [...payloadObjects];

    if (
      updatedPayloadObjects[roomIndex] &&
      updatedPayloadObjects[roomIndex].images[imageIndex]
    ) {
      const updatedPreviews = [...imagePreviews];

      // Clean up the preview URL
      if (
        updatedPreviews[roomIndex] &&
        updatedPreviews[roomIndex][imageIndex]
      ) {
        URL.revokeObjectURL(updatedPreviews[roomIndex][imageIndex]);
        updatedPreviews[roomIndex].splice(imageIndex, 1);
        setImagePreviews(updatedPreviews);
      }

      updatedPayloadObjects[roomIndex].images.splice(imageIndex, 1);
      setPayloadObjects(updatedPayloadObjects);
    }
  };

  const handleAccessInfoChange =
    (field: AccessInfoField) => (event: ChangeEvent<HTMLInputElement>) => {
      setAccessInfo((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const parseOptionalFloat = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const parseOptionalInt = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return undefined;
    return Math.trunc(parsed);
  };

  const buildMoveDetailsBody = () => {
    const body: {
      origin_stairs?: number;
      origin_elevator?: number;
      origin_long_carry?: number;
      destination_stairs?: number;
      destination_elevator?: number;
      destination_long_carry?: number;
    } = {};

    const originStairs = parseOptionalFloat(accessInfo.originStairs);
    if (originStairs !== undefined) body.origin_stairs = originStairs;

    const originElevator = parseOptionalInt(accessInfo.originElevator);
    if (originElevator !== undefined) body.origin_elevator = originElevator;

    const originLongCarry = parseOptionalInt(accessInfo.originLongCarry);
    if (originLongCarry !== undefined) {
      body.origin_long_carry = originLongCarry;
    }

    const destinationStairs = parseOptionalFloat(accessInfo.destinationStairs);
    if (destinationStairs !== undefined) {
      body.destination_stairs = destinationStairs;
    }

    const destinationElevator = parseOptionalInt(
      accessInfo.destinationElevator
    );
    if (destinationElevator !== undefined) {
      body.destination_elevator = destinationElevator;
    }

    const destinationLongCarry = parseOptionalInt(
      accessInfo.destinationLongCarry
    );
    if (destinationLongCarry !== undefined) {
      body.destination_long_carry = destinationLongCarry;
    }

    return body;
  };

  return (
    <>
      <Grid container marginX={"auto"}>
        <Grid item xs={12} md={8} marginX={"auto"}>
          {isLoading && (
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isLoading}
              onClick={() => {
                setIsLoading(true);
              }}
            >
              <Box sx={{ width: "50%" }}>
                <LinearProgress />
                <Typography
                  fontSize={16}
                  fontWeight={500}
                  textAlign="center"
                  mt={1}
                >
                  {loadingMessage &&
                    loadingMessage.split("\n").map((line, index) => (
                      <Typography key={index} component="span" display="block">
                        {line}
                      </Typography>
                    ))}
                </Typography>
              </Box>
            </Backdrop>
          )}
          <Box
            sx={{
              width: { xs: "90vw", sm: "80vw", md: "70vw", lg: "60vw" },
              height: isMobile ? "90vh" : "auto",
              p: { xs: 2, sm: 4 },
              mt: { xs: 3, sm: 5 },
              border: "1px solid #0000001A",
              borderRadius: "8px",
              overflow: "auto",
              backgroundColor: "#FBFBFB",
            }}
          >
            <Stack
              alignSelf={"center"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <img
                src="https://meatheadmovers.com/wp-content/uploads/2024/03/desktoplogo.png"
                width={280}
                alt="Logo"
              />
            </Stack>
            <Stack
              sx={{
                alignItems: isMobile ? "center" : "flex-start",
                overflow: "auto",
                mb: 3, // Margin bottom for spacing
              }}
            >
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
                sx={{ mb: 2 }}
              >
                <Stack>
                  <Typography fontSize={isMobile ? 18 : 24} fontWeight={500}>
                    Upload Photos
                  </Typography>
                  <Typography fontSize={isMobile ? 10 : 16} color={"#646464"}>
                    Our AI will analyse your photos of each room,
                    <Typography fontSize={isMobile ? 10 : 16} color={"#646464"}>
                      garage, patio etc in one click
                    </Typography>
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Stack
              sx={{
                height: { xs: "auto", sm: "auto", md: "auto" },
                p: { xs: 2, sm: 4 },
                mt: { xs: 3, sm: 5 },
                border: "1px solid #0000001A",
                borderRadius: "8px",
                overflow: "auto",
                backgroundColor: "#FFFFFF",
              }}
              spacing={2}
              mt={2}
              justifyContent={"center"}
            >
              {payloadObjects.map((payload, payloadIndex) => (
                <>
                  <Stack
                    alignItems={"center"}
                    spacing={2}
                    key={payloadIndex}
                    width="100%"
                    marginY={5}
                  >
                    {payloadIndex === payloadObjects.length - 1 && (
                      <Button
                        disabled={
                          payloadObjects.length > 0 &&
                          (payload.title === "" ||
                            payload.images.some((image) => image.type === ""))
                        }
                        sx={{
                          width: { xs: "100%", sm: 180 },
                          backgroundColor: "#FFD41D",
                          borderRadius: 12,
                          alignSelf: { xs: "center", sm: "flex-start" },
                          marginBottom: { xs: 2, sm: 0 },
                        }}
                        variant="contained"
                        onClick={addPayloadObject}
                      >
                        <Typography color={"#000000"} fontSize={12}>
                          + Add more rooms
                        </Typography>
                      </Button>
                    )}
                    <Stack
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      width={"100%"}
                      direction={isMobile ? "column" : "row"}
                      spacing={1}
                    >
                      <TextField
                        variant="outlined"
                        type="text"
                        label="Enter Room Name"
                        size="small"
                        sx={{
                          backgroundColor: "#FBFBFB",
                          fontSize: { xs: 14, sm: 16 },
                          width: "50%",
                        }}
                        value={payload.title}
                        onChange={(e) =>
                          handleTitleChange1(payloadIndex, e.target.value)
                        }
                      />
                      {payloadIndex !== 0 && (
                        <Button
                          sx={{
                            width: 156,
                            height: 50,
                            borderRadius: 12,
                            backgroundColor: "#FFE3E3 !important",
                          }}
                          variant="contained"
                          color="error"
                          onClick={() => discardRoom(payloadIndex)}
                        >
                          <Typography color={"#FF3232"} fontSize={12}>
                            Delete Room
                          </Typography>
                        </Button>
                      )}
                    </Stack>
                    {payloadObjects.length > 0 &&
                    (payload.title === "" ||
                      payload.images.some((image) => image.type === "")) ? (
                      <Stack
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        spacing={2}
                        sx={{
                          padding: 2,
                          border: "2px dashed #0257CF",
                          borderRadius: "8px",
                          backgroundColor: "#E4F0FF6B",
                          height: 150,
                          width: "95%",
                        }}
                      >
                        <Typography
                          fontWeight={600}
                          fontSize={{ xs: 14, sm: 16 }}
                        >
                          Add Images
                        </Typography>
                        <Typography fontSize={{ xs: 10, sm: 12 }}>
                          Add 2-4 room photos to capture all large furniture
                        </Typography>
                      </Stack>
                    ) : (
                      <></>
                    )}

                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      alignItems="center"
                      spacing={2}
                      sx={{
                        width: "100%",
                        gap: 2,
                      }}
                    >
                      {payload.images.map((image: any, imageIndex) => (
                        <Stack
                          spacing={1}
                          alignItems="flex-start"
                          key={imageIndex}
                        >
                          {image.size === 0 && (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <IconButton
                                color="primary"
                                component="label"
                                style={{ display: "block", margin: "10px 0" }}
                              >
                                <Stack alignItems={"center"}>
                                  <AddAPhotoIcon />
                                  <input
                                    type="file"
                                    capture="environment"
                                    onChange={(e) =>
                                      addImageToPayload(
                                        payloadIndex,
                                        e,
                                        imageIndex
                                      )
                                    }
                                    accept="image/*"
                                    style={{ display: "none" }}
                                  />
                                  <Typography variant="caption">
                                    Take Photo
                                  </Typography>
                                </Stack>
                              </IconButton>

                              <IconButton
                                color="secondary"
                                component="label"
                                style={{ display: "block", margin: "10px 0" }}
                              >
                                <Stack alignItems={"center"}>
                                  <FileUploadIcon />
                                  <input
                                    type="file"
                                    onChange={(e) =>
                                      addImageToPayload(
                                        payloadIndex,
                                        e,
                                        imageIndex
                                      )
                                    }
                                    accept="image/*"
                                    style={{ display: "none" }}
                                  />
                                  <Typography variant="caption">
                                    Upload
                                  </Typography>
                                </Stack>
                              </IconButton>
                              {payload.images.length > 0 &&
                                payload.images[payload.images.length - 1] &&
                                payload.images[payload.images.length - 1]
                                  .type === "" && (
                                  <IconButton
                                    onClick={() =>
                                      discardImage(
                                        payloadIndex,
                                        payload.images.length - 1
                                      )
                                    }
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      padding: 1,
                                      // backgroundColor: "#ffebee",
                                      borderRadius: "8px",
                                      "&:hover": {
                                        backgroundColor: "#ffcdd2",
                                      },
                                    }}
                                    size="small"
                                  >
                                    <CancelIcon fontSize="small" />
                                    <Typography variant="caption">
                                      Cancel
                                    </Typography>
                                  </IconButton>
                                )}
                            </Stack>
                          )}

                          <Box
                            sx={{
                              position: "relative",
                              width: { xs: 60, sm: 124 },
                              maxHeight: { xs: 60, sm: 124 },
                              borderRadius: 2,
                              overflow: "hidden",
                            }}
                          >
                            {imagePreviews[payloadIndex] &&
                              imagePreviews[payloadIndex][imageIndex] && (
                                <Box
                                  sx={{
                                    position: "relative",
                                    width: { xs: 60, sm: 124 },
                                    maxHeight: { xs: 60, sm: 124 },
                                    borderRadius: 2,
                                    overflow: "hidden",
                                  }}
                                >
                                  <img
                                    src={
                                      imagePreviews[payloadIndex][imageIndex]
                                    }
                                    alt={`Preview ${imageIndex + 1}`}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: 12,
                                    }}
                                  />

                                  <IconButton
                                    onClick={() =>
                                      discardImage(payloadIndex, imageIndex)
                                    }
                                    sx={{
                                      position: "absolute",
                                      top: 4,
                                      right: 4,
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.8)",
                                      color: "black",
                                      zIndex: 1,
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(255, 255, 255, 1)",
                                      },
                                    }}
                                    size="small"
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                          </Box>
                        </Stack>
                      ))}
                    </Stack>

                    <Button
                      sx={{
                        width: 232,
                        borderRadius: 4,
                        height: 48,
                        marginBottom: 5,
                      }}
                      variant="contained"
                      onClick={() => addImageInput(payloadIndex)}
                      disabled={
                        payload.images.length > 0 &&
                        payload.images[payload.images.length - 1].type === ""
                      }
                    >
                      Add Image +
                    </Button>
                  </Stack>
                  <Divider />
                </>
              ))}
              {formError && <Alert severity="error">{formError}</Alert>}
              <Stack spacing={1} sx={{ width: "100%" }}>
                <Typography fontSize={16} fontWeight={600}>
                  Origin Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Origin stairs (floors)"
                      type="number"
                      size="small"
                      value={accessInfo.originStairs}
                      onChange={handleAccessInfoChange("originStairs")}
                      inputProps={{ step: 0.1, min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Origin elevator floors"
                      type="number"
                      size="small"
                      value={accessInfo.originElevator}
                      onChange={handleAccessInfoChange("originElevator")}
                      inputProps={{ step: 1, min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Origin long carry (yards)"
                      type="number"
                      size="small"
                      value={accessInfo.originLongCarry}
                      onChange={handleAccessInfoChange("originLongCarry")}
                      inputProps={{ step: 1, min: 0 }}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Typography fontSize={16} fontWeight={600}>
                  Destination Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Destination stairs (floors)"
                      type="number"
                      size="small"
                      value={accessInfo.destinationStairs}
                      onChange={handleAccessInfoChange("destinationStairs")}
                      inputProps={{ step: 0.1, min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Destination elevator floors"
                      type="number"
                      size="small"
                      value={accessInfo.destinationElevator}
                      onChange={handleAccessInfoChange("destinationElevator")}
                      inputProps={{ step: 1, min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Destination long carry (yards)"
                      type="number"
                      size="small"
                      value={accessInfo.destinationLongCarry}
                      onChange={handleAccessInfoChange("destinationLongCarry")}
                      inputProps={{ step: 1, min: 0 }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Stack>
              <Button
                onClick={() => {
                  handleUploadImages();
                }}
                variant="contained"
                disabled={isLoading}
                sx={{
                  width: 180,
                  height: 45,
                  backgroundColor: `${theme.palette.primary.main} !important`,
                  color: "#FFFFFF",
                  fontSize: 12,
                  fontWeight: 550,
                  alignSelf: "center",
                  borderRadius: 14,
                }}
              >
                {isLoading ? "Submitting..." : "Continue"}
              </Button>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default AddSupermoveInventory;
